/**
 * A sala de assistir junto.
 *
 * Porte de `lib/features/watch_party/data/models/watch_party_room.dart` e de
 * `.../data/services/watch_party_service.dart`. A sala inteira é uma ficha só,
 * `watch_party_rooms/{id}`, e o id sai dos dois slugs ordenados — os dois lados
 * calculam igual e caem no mesmo documento sem combinar nada antes.
 *
 * O uid do Firebase *é* o slug da pessoa (`server/src/firebase.js`: "We use the
 * partner slug, so uids stay stable across account recreations"), então o que o
 * Flutter grava como `hostUid` a web lê como `quem`, e a sala aberta no celular
 * é a mesma que abre aqui.
 *
 * Mora no compartilhado porque a sessão a dois é a mesma nos três lados: o
 * mesmo documento, o mesmo id calculado, as mesmas gravações. O que muda é
 * quem desenha o player em cima disso.
 */

import { useMemo } from 'react';
import { ligacao } from '../nucleo/ligacao';
import { usarSessao } from '../nucleo/sessao';
import { gravarData, lerData } from '../nucleo/tempo';
import { limparConversa } from './conversa';

export const COLECAO_SALAS = 'watch_party_rooms';
export const COLECAO_CONVERSA = 'watch_party_chats';
export const COLECAO_CONVERSA_TEMP = 'temporary_chats';

/** A ficha crua, do jeito que o Flutter grava. */
export interface FichaDaSala {
  hostUid?: string;
  hostName?: string;
  partnerUid?: string;
  partnerName?: string;
  mediaType?: string;
  tmdbId?: number | string;
  malId?: number | null;
  isAnime?: boolean;
  season?: number | null;
  episode?: number | null;
  title?: string;
  posterPath?: string;
  serverType?: string | null;
  serverName?: string | null;
  serverHost?: string | null;
  streamUrl?: string | null;
  subtitleUrl?: string | null;
  proxyEnabled?: boolean;
  state?: string;
  currentTime?: number;
  updatedAt?: string;
  updatedBy?: string;
  createdAt?: string;
  active?: boolean;
}

export interface Sala {
  id: string;
  anfitriao: string;
  anfitriaoNome: string;
  parceiro: string;
  parceiroNome: string;
  tipo: string;
  tmdbId: number;
  malId: number | null;
  anime: boolean;
  temporada: number | null;
  episodio: number | null;
  titulo: string;
  cartaz: string;
  servidorTipo: string | null;
  servidorNome: string | null;
  servidorHost: string | null;
  fluxo: string | null;
  legenda: string | null;
  proxy: boolean;
  estado: string;
  segundos: number;
  mexidoEm: Date;
  mexidoPor: string;
  criadaEm: Date;
  ativa: boolean;
}

/**
 * O id da sala. Ordenar antes é o que garante que os dois lados chegam à mesma
 * string, não importa quem abriu primeiro.
 */
export function idDaSala(a: string, b: string): string {
  return [a, b].sort().join('_');
}

/** Quem entrou e quem é a outra pessoa. */
export interface Dupla {
  eu: string;
  parceiro: string;
  parceiroNome: string;
}

/**
 * A dupla da sessão.
 *
 * Vazia numa casa de três, como o `AuthService.partnerUsername` do Flutter:
 * "as telas que usam isto guardam estado com dois donos e escolher uma das
 * outras seria marcar como visto para quem não viu".
 */
export function usarDupla(): Dupla {
  const { quem, casa } = usarSessao();
  return useMemo(() => {
    const outros = (casa?.pessoas ?? []).filter((p) => p.slug !== quem);
    const outra = outros.length === 1 ? outros[0] : null;
    return {
      eu: quem ?? '',
      parceiro: outra?.slug ?? '',
      parceiroNome: outra?.nome ?? '',
    };
  }, [casa, quem]);
}

/** Fichas antigas guardaram o `tmdbId` como texto; as duas formas entram. */
function numero(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : null;
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function lerSala(id: string, d: FichaDaSala | null): Sala | null {
  if (!d) return null;
  const agora = new Date();
  return {
    id,
    anfitriao: d.hostUid ?? '',
    anfitriaoNome: d.hostName ?? '',
    parceiro: d.partnerUid ?? '',
    parceiroNome: d.partnerName ?? '',
    tipo: d.mediaType ?? 'movie',
    tmdbId: numero(d.tmdbId) ?? 0,
    malId: numero(d.malId),
    anime: d.isAnime === true,
    temporada: numero(d.season),
    episodio: numero(d.episode),
    titulo: d.title ?? '',
    cartaz: d.posterPath ?? '',
    servidorTipo: d.serverType ?? null,
    servidorNome: d.serverName ?? null,
    servidorHost: d.serverHost ?? null,
    fluxo: d.streamUrl ?? null,
    legenda: d.subtitleUrl ?? null,
    proxy: d.proxyEnabled === true,
    estado: d.state ?? 'paused',
    segundos: typeof d.currentTime === 'number' ? d.currentTime : 0,
    mexidoEm: lerData(d.updatedAt) ?? agora,
    mexidoPor: d.updatedBy ?? '',
    criadaEm: lerData(d.createdAt) ?? agora,
    ativa: d.active === true,
  };
}

/** "0:23" / "1:02:45", para a tarja de sincronia. */
export function relogio(segundos: number): string {
  const total = Math.max(0, Math.round(segundos));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dois = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${dois(m)}:${dois(s)}` : `${m}:${dois(s)}`;
}

/**
 * O bastante para abrir uma sessão. Ou um título do catálogo, ou uma URL
 * direta — canal de IPTV, filme do Jellyfin, arquivo da lista.
 */
export interface Fonte {
  tmdbId: number;
  malId?: number | null;
  tipo: string;
  anime?: boolean;
  temporada?: number | null;
  episodio?: number | null;
  titulo: string;
  cartaz?: string;
  /** Quando existe, não há TMDB nenhum: a identidade da sessão é a URL. */
  fluxo?: string | null;
  /** `hls` para playlist, `embed` para uma página que toca sozinha. */
  servidorTipo?: string;
  servidorNome?: string | null;
  legenda?: string | null;
}

/** Já sabe o que tocar, ou é um botão vazio esperando escolha? */
export function daParaTocar(f: Fonte): boolean {
  return f.tmdbId !== 0 || Boolean(f.fluxo);
}

/**
 * A sala está tocando outra coisa?
 *
 * Uma fonte sem escolha nunca "difere": senão o cartão do painel trocaria a
 * sessão em andamento por nada.
 */
export function fonteDiferente(sala: Sala, f: Fonte): boolean {
  if (!daParaTocar(f)) return false;
  if (f.fluxo) return sala.fluxo !== f.fluxo;
  return (
    sala.fluxo !== null ||
    sala.tmdbId !== f.tmdbId ||
    (sala.temporada ?? null) !== (f.temporada ?? null) ||
    (sala.episodio ?? null) !== (f.episodio ?? null)
  );
}

/** Escreve só os campos passados, deixando o resto da sala como está. */
function remendar(id: string, campos: Record<string, unknown>): Promise<unknown> {
  return ligacao().armazem.gravar(COLECAO_SALAS, id, campos, true);
}

/**
 * Os campos de mídia de uma fonte.
 *
 * Gravados mesmo quando são nulos: a sala é escrita com mesclagem, e um campo
 * omitido deixaria a URL da sessão anterior viva na sessão nova — quem trocasse
 * um canal de IPTV por um filme continuaria vendo o canal.
 */
function midiaDe(f: Fonte): Record<string, unknown> {
  return {
    mediaType: f.tipo,
    tmdbId: f.tmdbId,
    malId: f.malId ?? null,
    isAnime: f.anime === true,
    season: f.temporada ?? null,
    episode: f.episodio ?? null,
    title: f.titulo,
    posterPath: f.cartaz ?? '',
    serverType: f.fluxo ? (f.servidorTipo ?? 'hls') : null,
    serverName: f.servidorNome ?? null,
    serverHost: null,
    streamUrl: f.fluxo ?? null,
    subtitleUrl: f.legenda ?? null,
    proxyEnabled: false,
  };
}

/**
 * Abre uma sala ou revive a que existe. O anfitrião é quem toca o botão, e a
 * ficha nasce pausada em zero para os dois começarem no mesmo lugar.
 */
export async function abrirSala(dupla: Dupla, f: Fonte): Promise<Sala> {
  const { eu, parceiro, parceiroNome } = dupla;
  const id = idDaSala(eu, parceiro);
  const agora = new Date();
  /*
   * O `hostName` é o slug e o `partnerName` é o nome de exibição — torto, mas
   * é o que o `start_watch_party_button.dart` grava (`auth.currentUser` de um
   * lado, `auth.partnerName` do outro). Endireitar aqui faria a ficha da web
   * diferir da do celular na mesma sala.
   */
  const ficha = {
    hostUid: eu,
    hostName: eu,
    partnerUid: parceiro,
    partnerName: parceiroNome || parceiro,
    ...midiaDe(f),
    state: 'paused',
    currentTime: 0,
    updatedAt: gravarData(agora),
    updatedBy: eu,
    createdAt: gravarData(agora),
    active: true,
  };

  await remendar(id, ficha);
  void ligacao().api.avisar('watch_party_invite', f.titulo);
  return lerSala(id, ficha as FichaDaSala) as Sala;
}

/**
 * Troca o filme da sala sem encerrar a sessão. Relança de propósito: engolir
 * deixava a sessão abrir com a sala antiga, sem canal e sem id de catálogo.
 */
export async function trocarMidia(id: string, eu: string, f: Fonte): Promise<void> {
  await remendar(id, {
    ...midiaDe(f),
    state: 'paused',
    currentTime: 0,
    updatedAt: gravarData(),
    updatedBy: eu,
  });
}

/**
 * Publica um estado novo. O anfitrião chama ao dar play, pause ou pular; quem
 * assiste, ao realinhar. Falha calada: um pulso perdido não derruba o player, e
 * o próximo passa.
 */
export async function publicarEstado(
  id: string,
  estado: string,
  segundos: number,
  eu: string,
): Promise<void> {
  try {
    await remendar(id, {
      state: estado,
      currentTime: segundos,
      updatedAt: gravarData(),
      updatedBy: eu,
      active: true,
    });
  } catch {
    /* melhor esforço */
  }
}

/** O "ainda estou aqui", barato e espaçado. */
export async function pulsar(
  id: string,
  estado: string,
  segundos: number,
  eu: string,
): Promise<void> {
  try {
    await remendar(id, {
      state: estado,
      currentTime: segundos,
      updatedAt: gravarData(),
      updatedBy: eu,
    });
  } catch {
    /* melhor esforço */
  }
}

/** Troca o servidor sem encerrar a sessão, zerando o ponto para os dois. */
export async function trocarServidor(
  id: string,
  eu: string,
  servidor: {
    tipo?: string | null;
    nome?: string | null;
    host?: string | null;
    fluxo?: string | null;
    legenda?: string | null;
  },
): Promise<void> {
  try {
    await remendar(id, {
      serverType: servidor.tipo ?? null,
      serverName: servidor.nome ?? null,
      serverHost: servidor.host ?? null,
      streamUrl: servidor.fluxo ?? null,
      subtitleUrl: servidor.legenda ?? null,
      proxyEnabled: false,
      state: 'paused',
      currentTime: 0,
      updatedAt: gravarData(),
      updatedBy: eu,
    });
  } catch {
    /* melhor esforço */
  }
}

/**
 * Encerra a sessão. A ficha fica onde está, para o painel ainda dizer "sessão
 * encerrada há pouco", mas já o bastante para o outro lado fechar o player.
 */
export async function encerrarSala(id: string): Promise<void> {
  try {
    await remendar(id, { active: false, state: 'paused', updatedAt: gravarData() });
  } catch {
    /* melhor esforço */
  }
}

/**
 * Cada sessão começa de conversa limpa. As duas conversas usam o id da sala,
 * então limpar aqui zera as duas de uma vez.
 */
export async function limparConversas(id: string): Promise<void> {
  for (const colecao of [COLECAO_CONVERSA, COLECAO_CONVERSA_TEMP]) {
    try {
      await limparConversa(colecao, id);
    } catch {
      /* uma conversa que não limpou não impede a sessão de começar */
    }
  }
}
